import type { HTMLAttributes, JSX } from 'react';

export type CardVariant = 'default' | 'outlined' | 'elevated';
export type CardElevation = 'none' | 'sm' | 'md' | 'lg';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  elevation?: CardElevation;
}

const getVariantClasses = (variant: CardVariant): string => {
  switch (variant) {
    case 'default':
      return 'bg-white border border-gray-200';
    case 'outlined':
      return 'bg-white border-2 border-gray-300';
    case 'elevated':
      return 'bg-white border border-gray-100';
  }
};

const paddingStyles: Record<NonNullable<CardProps['padding']>, string> = {
  none: 'p-0',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

const elevationStyles: Record<CardElevation, string> = {
  none: '',
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg',
};

export function Card({
  variant = 'default',
  padding = 'md',
  elevation,
  className = '',
  style,
  children,
  ...props
}: CardProps): JSX.Element {
  const baseStyles = 'rounded-lg';

  // Default elevation based on variant if not explicitly set
  const effectiveElevation = elevation ?? (variant === 'elevated' ? 'lg' : 'none');
  const shadowClass = elevationStyles[effectiveElevation];

  const combinedClassName = [
    baseStyles,
    paddingStyles[padding],
    shadowClass,
    getVariantClasses(variant),
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={combinedClassName} style={style} {...props}>
      {children}
    </div>
  );
}
